import { Component, Fragment, type ErrorInfo, type ReactNode } from "react";
import { RefreshCw, TriangleAlert } from "lucide-react";
import { useT } from "../../i18n/I18nProvider";
import { Btn, EmptyState } from "../ui";

/**
 * The only thing standing between one throwing component and a blank screen.
 *
 * React unmounts the whole tree below the nearest boundary when a render
 * throws, and with no boundary at all "the nearest one" is the root - the user
 * gets an empty `#root` and nothing else. That is not theoretical: a list
 * endpoint answering with a body one level thinner than expected took the
 * entire CRM down, console silent, because the throw happened inside a
 * `Promise.all` render path.
 *
 * Two deliberate choices:
 *  - The boundary is NOT at the root. It sits around the routed area, so the
 *    header and the rail survive and the operator can navigate somewhere that
 *    still works instead of reloading and landing on the same broken page.
 *  - Retry REMOUNTS the subtree (see `nonce`) rather than only clearing the
 *    error. A component that threw halfway through an effect can be left with
 *    state that will throw again the moment it re-renders; a fresh mount is the
 *    only reset that covers that.
 */

interface Props {
  children: ReactNode;
  /**
   * Changing this clears a caught error. Pass the pathname: navigating away is
   * already a fresh attempt, and leaving the fallback up on the new route would
   * blame the wrong page.
   */
  resetKey?: string;
  fallback: (error: Error, retry: () => void) => ReactNode;
}

interface State {
  error: Error | null;
  /** Bumped on retry to force a remount of the children. */
  nonce: number;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, nonce: 0 };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    // A thrown string or object is rare but legal, and the fallback should not
    // have to guess whether `.message` exists.
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // A caught error never reaches window.onerror, so without this line a
    // production failure leaves no trace anywhere at all.
    console.error("[crm] render failed", error, info.componentStack);
  }

  override componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState((s) => ({ error: null, nonce: s.nonce + 1 }));
    }
  }

  override render() {
    const { error, nonce } = this.state;
    if (error) {
      return this.props.fallback(error, () => this.setState((s) => ({ error: null, nonce: s.nonce + 1 })));
    }
    return <Fragment key={nonce}>{this.props.children}</Fragment>;
  }
}

/**
 * What the operator sees instead of the page.
 *
 * The technical message is shown, not hidden: this app is operator-only, and
 * "something went wrong" with the one useful sentence stripped out just means
 * the report that reaches us says "something went wrong" too.
 */
function ErrorPane({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const t = useT();
  return (
    <div className="crm-card">
      <EmptyState
        icon={TriangleAlert}
        title={t("error.pane.title")}
        body={t("error.pane.body")}
        action={
          <div className="flex flex-col items-center gap-4">
            <Btn tone="dark" onClick={onRetry}>
              <RefreshCw className="size-4" />
              {t("error.pane.retry")}
            </Btn>
            {error.message ? (
              <code className="crm-err-msg">{error.message}</code>
            ) : null}
          </div>
        }
      />
    </div>
  );
}

/** The boundary as the layout uses it: around the routes, keyed on the path. */
export function RouteErrorBoundary({ resetKey, children }: { resetKey: string; children: ReactNode }) {
  return (
    <ErrorBoundary resetKey={resetKey} fallback={(error, retry) => <ErrorPane error={error} onRetry={retry} />}>
      {children}
    </ErrorBoundary>
  );
}

/**
 * The last resort, around the whole app.
 *
 * The route boundary covers the routed area, but the header, the rail, the
 * signals provider and the call provider all render outside it and all read
 * from the API - a throw in any of them would still empty the screen. This one
 * cannot keep the navigation alive (there is none left to keep), so it centres
 * the same pane on an otherwise empty page. Retry remounts the app, which
 * re-runs the session fetch: a soft reload without losing the URL.
 */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={(error, retry) => (
        <div className="flex min-h-dvh items-center justify-center bg-background p-6">
          <div className="w-full max-w-xl">
            <ErrorPane error={error} onRetry={retry} />
          </div>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
